import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useMemo, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/src/components/ui/card'
import { Button } from '@/src/components/ui/button'
import { Badge } from '@/src/components/ui/badge'
import { Spinner } from '@/src/components/ui/spinner'
import { toast } from 'sonner'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { listUnassignedDrawings, assignDrawingsToProject } from '@/src/api/drawing'
import { listProjects, createProject } from '@/src/api/project'
import { useAuth } from '@/src/components/AuthContextProvider'
import { useTranslation } from '@/src/i18n/client'
import { PERMISSIONS } from '@/permission.config'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import {
  TrayIcon,
  FolderIcon,
  CheckCircleIcon,
  FunnelIcon,
} from '@phosphor-icons/react'
import type { DrawingListItem } from '@/src/types/drawing-list-item'
import { filterByTime, type TimeFilter } from '@/src/components/manage/organize/utils'
import { FilterBar } from '@/src/components/manage/organize/FilterBar'
import { DraggableDrawingCard } from '@/src/components/manage/organize/DraggableDrawingCard'
import { DrawingDragPreview } from '@/src/components/manage/organize/DrawingDragPreview'
import { ProjectDropZone } from '@/src/components/manage/organize/ProjectDropZone'
import { NewProjectDropZone } from '@/src/components/manage/organize/NewProjectDropZone'
import { ProjectDialog } from '@/src/components/manage/organize/ProjectDialog'
import { resolveDrawingName, resolveDrawingNumber, resolveDrawingCompany } from '@/src/lib/drawingHelpers'

export const Route = createFileRoute('/_manage/manage/organize/')({
  component: OrganizePage,
})

function OrganizePage() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('manage')
  const { user, permissions, isAdmin } = useAuth()
  const navigate = useNavigate()
  
  // Check permission client-side
  useEffect(() => {
    const hasAccess = permissions.includes(PERMISSIONS.PROJECTS_MANAGE) || permissions.includes('*') || isAdmin
    if (!hasAccess) {
      navigate({ to: '/not-permitted' })
    }
  }, [permissions, isAdmin, navigate])

  const [selectedDrawings, setSelectedDrawings] = useState<Set<string>>(new Set())
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null)
  const [activeDrawingId, setActiveDrawingId] = useState<string | null>(null)
  const [overProjectId, setOverProjectId] = useState<string | null>(null)
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all')
  const [drawingSearch, setDrawingSearch] = useState('')
  const [projectSearch, setProjectSearch] = useState('')
  const [projectTimeFilter, setProjectTimeFilter] = useState<TimeFilter>('all')

  // Upsert project dialog state
  const [showUpsertDialog, setShowUpsertDialog] = useState(false)
  const [upsertMode, setUpsertMode] = useState<'create' | 'edit'>('create')
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)
  const [pendingDrawingIds, setPendingDrawingIds] = useState<string[]>([])

  // Fetch unassigned drawings
  const { data: unassignedData, isLoading: drawingsLoading } = useQuery({
    queryKey: ['drawing', 'unassigned'],
    queryFn: () => listUnassignedDrawings({ data: { limit: 100 } }),
  })

  // Fetch user's projects
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['project', 'list'],
    queryFn: () => listProjects({ data: {} }),
  })

  // Fetch project details when editing
  const { data: editingProject } = useQuery({
    queryKey: ['project', editingProjectId],
    queryFn: async () => {
      if (!editingProjectId) return null
      const projectsList = await listProjects({ data: {} })
      return projectsList?.find((p: any) => p.id === editingProjectId) || null
    },
    enabled: !!editingProjectId && upsertMode === 'edit',
  })

  const allUnassignedDrawings = useMemo(
    () => unassignedData?.drawings || [],
    [unassignedData?.drawings]
  )
  const unassignedDrawings = useMemo(() => {
    let filtered = filterByTime(allUnassignedDrawings, timeFilter)

    // Apply search filter
    if (drawingSearch.trim()) {
      const search = drawingSearch.toLowerCase()
      filtered = filtered.filter(d => {
        const name = resolveDrawingName(d)
        const number = resolveDrawingNumber(d)
        const company = resolveDrawingCompany(d)
        return (
          name?.toLowerCase().includes(search) ||
          number?.toLowerCase().includes(search) ||
          company?.toLowerCase().includes(search)
        )
      })
    }

    return filtered
  }, [allUnassignedDrawings, timeFilter, drawingSearch])

  // Filter projects by search and time
  const allProjects = useMemo(
    () => projects || [],
    [projects]
  )
  const filteredProjects = useMemo(() => {
    let filtered = allProjects

    // Apply search filter
    if (projectSearch.trim()) {
      const search = projectSearch.toLowerCase()
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(search) ||
        p.company?.toLowerCase().includes(search) ||
        p.description?.toLowerCase().includes(search)
      )
    }

    // Apply time filter
    if (projectTimeFilter !== 'all') {
      const now = new Date()
      const cutoff = new Date()

      switch (projectTimeFilter) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0)
          break
        case 'hour':
          cutoff.setTime(now.getTime() - 60 * 60 * 1000)
          break
        case '10min':
          cutoff.setTime(now.getTime() - 10 * 60 * 1000)
          break
      }

      filtered = filtered.filter(p => p.createdAt && new Date(p.createdAt) >= cutoff)
    }

    return filtered
  }, [allProjects, projectSearch, projectTimeFilter])

  // Assign mutation
  const assignMutation = useMutation({
    mutationFn: (params: { drawingIds: string[]; projectId: string | null }) =>
      assignDrawingsToProject({ data: params }),
    onSuccess: (_, variables) => {
      const count = variables.drawingIds.length
      const projectName = variables.projectId
        ? projects?.find(p => p.id === variables.projectId)?.name || 'project'
        : 'unassigned'

      if (variables.projectId) {
        toast.success(`${count} drawing${count !== 1 ? 's' : ''} assigned to ${projectName}`)
      } else {
        toast.success(`${count} drawing${count !== 1 ? 's' : ''} unassigned`)
      }

      setSelectedDrawings(new Set())
      queryClient.invalidateQueries({ queryKey: ['drawing'] })
      queryClient.invalidateQueries({ queryKey: ['project'] })
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to assign drawings')
    },
  })

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle selection
  const handleSelect = (id: string, multi: boolean, range: boolean) => {
    setSelectedDrawings(prev => {
      const next = new Set(prev)

      if (range && lastSelectedId && unassignedDrawings.length > 0) {
        // Range selection: select all items between last selected and current
        const ids = unassignedDrawings.map(d => d.id)
        const lastIndex = ids.indexOf(lastSelectedId)
        const currentIndex = ids.indexOf(id)

        if (lastIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastIndex, currentIndex)
          const end = Math.max(lastIndex, currentIndex)

          for (let i = start; i <= end; i++) {
            next.add(ids[i])
          }
        }
      } else if (multi) {
        // Multi-select: toggle the item
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
      } else {
        // Single select: clear and select only this item (or deselect if it's the only one)
        if (next.has(id) && next.size === 1) {
          next.clear()
        } else {
          next.clear()
          next.add(id)
        }
      }

      return next
    })

    // Update last selected ID for range selection (but not when deselecting)
    if (!multi || !selectedDrawings.has(id)) {
      setLastSelectedId(id)
    }
  }

  // Select all / clear selection
  const handleSelectAll = () => {
    if (selectedDrawings.size === unassignedDrawings.length) {
      setSelectedDrawings(new Set())
    } else {
      setSelectedDrawings(new Set(unassignedDrawings.map(d => d.id)))
    }
  }

  // Get active drawing(s) for drag overlay
  const activeDragDrawings = useMemo(() => {
    if (!activeDrawingId) return []
    // If active drawing is selected, drag all selected
    if (selectedDrawings.has(activeDrawingId)) {
      return unassignedDrawings.filter(d => selectedDrawings.has(d.id))
    }
    // Otherwise just drag the active one
    return unassignedDrawings.filter(d => d.id === activeDrawingId)
  }, [activeDrawingId, selectedDrawings, unassignedDrawings])

  // Get pending drawings from IDs for new project dialog
  const pendingDrawingsData = useMemo(() => {
    if (pendingDrawingIds.length === 0) return []
    return allUnassignedDrawings.filter(d => pendingDrawingIds.includes(d.id))
  }, [pendingDrawingIds, allUnassignedDrawings])

  // DnD handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveDrawingId(event.active.id as string)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event
    if (over?.data?.current?.type === 'project') {
      setOverProjectId(over.data.current.projectId)
    } else if (over?.data?.current?.type === 'new-project') {
      setOverProjectId('new-project')
    } else if (over?.id === 'unassigned') {
      setOverProjectId('unassigned')
    } else {
      setOverProjectId(null)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveDrawingId(null)
    setOverProjectId(null)

    if (!over) return

    // Get drawing IDs to assign
    const drawingIds = selectedDrawings.has(active.id as string)
      ? Array.from(selectedDrawings)
      : [active.id as string]

    // Handle new project creation
    if (over.data?.current?.type === 'new-project') {
      setPendingDrawingIds(drawingIds)
      setUpsertMode('create')
      setEditingProjectId(null)
      setShowUpsertDialog(true)
      setSelectedDrawings(new Set())
      return
    }

    // Determine target project
    if (over.data?.current?.type === 'project') {
      const projectId = over.data.current.projectId
      assignMutation.mutate({ drawingIds, projectId })
    }
  }

  const isLoading = drawingsLoading || projectsLoading

  return (
    <div className="bg-background text-foreground">
      <AdminToolbar>
        <AdminToolbar.Left>
          <AdminToolbar.Title as="h1" className="text-lg font-semibold">{t('organize.title')}</AdminToolbar.Title>
        </AdminToolbar.Left>
      </AdminToolbar>

      <ProjectDialog
        open={showUpsertDialog}
        onOpenChange={(open) => {
          setShowUpsertDialog(open)
          if (!open) {
            setEditingProjectId(null)
            setPendingDrawingIds([])
          }
        }}
        mode={upsertMode}
        projectId={editingProjectId}
        project={editingProject}
        isLoading={upsertMode === 'edit' && !editingProject}
        pendingDrawings={pendingDrawingsData}
      />

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Spinner size={40} />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Unassigned Drawings Panel */}
            <Card className="lg:col-span-1" style={{ minHeight: '480px' }}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrayIcon className="w-5 h-5" />
                  {t('organize.unassigned.title')}
                  {allUnassignedDrawings.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {(drawingSearch || timeFilter !== 'all') && allUnassignedDrawings.length !== unassignedDrawings.length
                        ? `${unassignedDrawings.length} / ${allUnassignedDrawings.length}`
                        : unassignedDrawings.length
                      }
                    </Badge>
                  )}
                  {allUnassignedDrawings.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto h-7 text-xs"
                      onClick={handleSelectAll}
                      disabled={unassignedDrawings.length === 0}
                    >
                      {selectedDrawings.size === unassignedDrawings.length && unassignedDrawings.length > 0 ? t('organize.unassigned.clearSelection') : t('organize.unassigned.selectAll')}
                      {selectedDrawings.size > 0 && (
                        <Badge variant="secondary" className="ml-1.5 px-1.5 py-0">
                          {selectedDrawings.size}
                        </Badge>
                      )}
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-visible flex flex-col h-full">
                {allUnassignedDrawings.length > 0 && (
                  <div className="mb-3">
                    <FilterBar
                      searchValue={drawingSearch}
                      onSearchChange={setDrawingSearch}
                      timeFilter={timeFilter}
                      onTimeFilterChange={setTimeFilter}
                      searchPlaceholder={t('organize.filterBar.searchDrawingsPlaceholder')}
                    />
                  </div>
                )}
                {unassignedDrawings.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    {(drawingSearch || timeFilter !== 'all') && allUnassignedDrawings.length > 0 ? (
                      <>
                        <FunnelIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                        <p className="font-medium">{t('organize.unassigned.empty.filteredTitle')}</p>
                        <p className="text-sm mt-1">{t('organize.unassigned.empty.filteredBody')}</p>
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => { setTimeFilter('all'); setDrawingSearch(''); }}
                        >
                          {t('organize.unassigned.clearFiltersButton')}
                        </Button>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-500" />
                        <p className="font-medium">{t('organize.unassigned.empty.allOrganizedTitle')}</p>
                        <p className="text-sm mt-1">{t('organize.unassigned.empty.allOrganizedBody')}</p>
                      </>
                    )}
                  </div>
                ) : (
                  <SortableContext
                    items={unassignedDrawings.map(d => d.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-125 overflow-y-auto overflow-x-hidden py-1">
                      {unassignedDrawings.map((drawing) => (
                        <DraggableDrawingCard
                          key={drawing.id}
                          drawing={drawing}
                          isSelected={selectedDrawings.has(drawing.id)}
                          onSelect={handleSelect}
                          isDragging={activeDragDrawings.some(d => d.id === drawing.id)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                )}

                <div className="flex items-center gap-4 pt-4 text-xs text-muted-foreground flex-wrap mt-auto border-t">
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-background border rounded text-xs">Click</kbd>
                    <span>{t('organize.hints.selectDrawing')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-background border rounded text-xs">Drag</kbd>
                    <span>{t('organize.hints.assignToProject')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4 pt-2 text-xs text-muted-foreground flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-background border rounded text-xs">Ctrl</kbd>
                    <span>+</span>
                    <kbd className="px-1.5 py-0.5 bg-background border rounded text-xs">Click</kbd>
                    <span>{t('organize.hints.multiSelect')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <kbd className="px-1.5 py-0.5 bg-background border rounded text-xs">Shift</kbd>
                    <span>+</span>
                    <kbd className="px-1.5 py-0.5 bg-background border rounded text-xs">Click</kbd>
                    <span>{t('organize.hints.rangeSelect')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Projects Panel */}
            <Card className="lg:col-span-1" style={{ minHeight: '480px' }}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <FolderIcon className="w-5 h-5" />
                  {t('projects.title')}
                  {filteredProjects.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {projectSearch || projectTimeFilter !== 'all'
                        ? `${filteredProjects.length} / ${allProjects.length}`
                        : allProjects.length
                      }
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {allProjects.length > 0 && (
                  <div className="mb-3">
                    <FilterBar
                      searchValue={projectSearch}
                      onSearchChange={setProjectSearch}
                      timeFilter={projectTimeFilter}
                      onTimeFilterChange={setProjectTimeFilter}
                      searchPlaceholder={t('organize.filterBar.searchProjectsPlaceholder')}
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 max-h-105 overflow-y-auto overflow-x-hidden py-1 px-1">
                  <NewProjectDropZone
                    isOver={overProjectId === 'new-project'}
                    onClick={() => {
                      setUpsertMode('create')
                      setEditingProjectId(null)
                      setPendingDrawingIds([])
                      setShowUpsertDialog(true)
                    }}
                  />

                  {filteredProjects.length === 0 && allProjects.length > 0 ? (
                    <div className="col-span-2 text-center py-6 text-muted-foreground">
                      <FunnelIcon className="w-8 h-8 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-sm">{t('organize.projects.noMatching')}</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2"
                        onClick={() => { setProjectSearch(''); setProjectTimeFilter('all'); }}
                      >
                        {t('organize.unassigned.clearFiltersButton')}
                      </Button>
                    </div>
                  ) : filteredProjects.length === 0 ? (
                    <div className="col-span-2 text-center py-6 text-muted-foreground">
                      <p className="text-sm">No projects yet</p>
                    </div>
                  ) : (
                    filteredProjects.map((project) => (
                      <ProjectDropZone
                        key={project.id}
                        project={project}
                        isOver={overProjectId === project.id}
                        drawingCount={project._count?.drawings || 0}
                        onClick={() => {
                          setUpsertMode('edit')
                          setEditingProjectId(project.id)
                          setShowUpsertDialog(true)
                        }}
                      />
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeDrawingId && (
              <DrawingDragPreview drawings={activeDragDrawings} />
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  )
}
